import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { getCalendar, requestSync } from '../api/ownerApi';
import { ownerKeys } from '../api/queryKeys';
import type { CalendarBooking } from '../api/schemas';
import { BookingSheet } from '../booking/BookingSheet';
import { useLocale, useT } from '../i18n/useT';
import { useUnit } from '../units/UnitContext';
import { Sheet } from '../ui/Sheet';
import { BookingDetailSheet } from './BookingDetailSheet';
import { formatMonth } from './format';
import { Legend } from './Legend';
import { MonthGrid } from './MonthGridView';
import { buildCells, canGoTo, gridRange, monthDays, type DayCell, type IsoDate } from './monthGrid';
import { SyncLine } from './SyncLine';
import { useToday } from './useToday';

const POLL_MS = 5000;

type BookingTarget = { initialFrom?: IsoDate };

export const CalendarScreen = (): JSX.Element => {
  const t = useT();
  const locale = useLocale();
  const { unit } = useUnit();
  const queryClient = useQueryClient();
  const today = useToday();
  const [{ year, month }, setMonth] = useState(() => ({
    year: Number(today.slice(0, 4)),
    month: Number(today.slice(5, 7))
  }));
  // A block's id is a VisBook period id, so a booking is picked by type and id together.
  const [selected, setSelected] = useState<Pick<CalendarBooking, 'id' | 'type'> | null>(null);
  const [booking, setBooking] = useState<BookingTarget | null>(null);
  const [lockedOpen, setLockedOpen] = useState(false);

  const days = useMemo(() => monthDays(year, month), [year, month]);
  const { from, to } = gridRange(days);
  const key = ownerKeys.calendar(unit.id, from, to);
  const calendar = useQuery({
    queryKey: key,
    queryFn: () => getCalendar(unit.id, from, to),
    refetchInterval: (query) => (query.state.data?.sync.state === 'running' ? POLL_MS : false)
  });

  // One sync request per unit. The ref (not the effect) is the guard, so a remount in StrictMode
  // or a month change does not ask again.
  const syncedUnit = useRef<number | null>(null);
  useEffect(() => {
    if (syncedUnit.current === unit.id) {
      return;
    }
    syncedUnit.current = unit.id;
    requestSync(unit.id)
      .then((sync) => {
        // The calendar may have been fetched before the sync started; pick up `running`.
        if (sync.state === 'running') {
          return queryClient.invalidateQueries({ queryKey: ownerKeys.calendarOf(unit.id) });
        }
      })
      .catch(() => {
        // The sync is a convenience; the calendar still shows what is stored.
      });
  }, [unit.id, queryClient]);

  // Leaving `running` refetches once, so bookings the sync just wrote show up.
  const syncState = calendar.data?.sync.state;
  const previous = useRef<{ key: string; state: string | undefined }>({
    key: key.join('/'),
    state: syncState
  });
  const refetch = calendar.refetch;
  useEffect(() => {
    const now = { key: key.join('/'), state: syncState };
    const before = previous.current;
    previous.current = now;
    if (before.key === now.key && before.state === 'running' && now.state !== 'running') {
      void refetch();
    }
  }, [key, syncState, refetch]);

  const bookings = calendar.data?.bookings;
  const locked = calendar.data?.locked;
  const cells = useMemo(
    () => buildCells(days, month, today, bookings ?? [], locked ?? []),
    [days, month, today, bookings, locked]
  );
  const detail =
    selected === null
      ? undefined
      : bookings?.find((b) => b.id === selected.id && b.type === selected.type);

  const go = (delta: -1 | 1): void => {
    const index = year * 12 + (month - 1) + delta;
    const nextYear = Math.floor(index / 12);
    const nextMonth = (index % 12) + 1;
    if (canGoTo(nextYear, nextMonth, today)) {
      setMonth({ year: nextYear, month: nextMonth });
    }
  };

  const onDayTap = (cell: DayCell): void => {
    // The night is what a day stands for: a locked one explains itself, even on a departure.
    if (cell.locked) {
      setLockedOpen(true);
      return;
    }
    const half = cell.full ?? cell.arriving ?? cell.leaving;
    if (half === undefined) {
      // Past days and unbookable units never open the sheet.
      if (unit.bookable && cell.date >= today) {
        setBooking({ initialFrom: cell.date });
      }
    } else {
      setSelected({ id: half.bookingId, type: half.type });
    }
  };

  return (
    <div className="ow-calendar">
      <div className="ow-month-nav">
        <button
          type="button"
          className="ow-month-step"
          aria-label={t('Previous month')}
          disabled={!canGoTo(...prevOf(year, month), today)}
          onClick={() => {
            go(-1);
          }}
        >
          ‹
        </button>
        <h1 className="ow-month-title" aria-live="polite">
          {formatMonth(year, month, locale)}
        </h1>
        <button
          type="button"
          className="ow-month-step"
          aria-label={t('Next month')}
          disabled={!canGoTo(...nextOf(year, month), today)}
          onClick={() => {
            go(1);
          }}
        >
          ›
        </button>
      </div>
      {calendar.data && <SyncLine sync={calendar.data.sync} />}
      {calendar.isError && (
        <div className="ow-error-row" role="alert">
          <p className="ow-error">{t('Something went wrong. Please try again.')}</p>
          <button
            type="button"
            className="ow-btn-secondary"
            onClick={() => {
              void calendar.refetch();
            }}
          >
            {t('Try again')}
          </button>
        </div>
      )}
      <MonthGrid
        cells={cells}
        onDayTap={onDayTap}
        onSwipe={go}
        busy={calendar.data === undefined}
      />
      <Legend locked={cells.some((cell) => cell.locked)} />
      {unit.bookable ? (
        <div className="ow-book-bar">
          <button
            type="button"
            className="ow-btn-primary ow-book-button"
            onClick={() => {
              setBooking({});
            }}
          >
            {t('Book stay')}
          </button>
        </div>
      ) : (
        <p className="ow-muted ow-book-note">{t("This unit can't be booked yet.")}</p>
      )}
      <Sheet
        open={detail !== undefined}
        title={detail?.type === 'block' ? t('Block details') : t('Booking details')}
        onClose={() => {
          setSelected(null);
        }}
      >
        {detail && (
          <BookingDetailSheet
            booking={detail}
            unitId={unit.id}
            today={today}
            bookings={bookings ?? []}
            locked={locked ?? []}
            onDeleted={() => {
              setSelected(null);
            }}
          />
        )}
      </Sheet>
      <Sheet
        open={lockedOpen}
        title={t('Fully booked')}
        onClose={() => {
          setLockedOpen(false);
        }}
      >
        <p>{t('All units of this type are booked on these nights.')}</p>
      </Sheet>
      <BookingSheet
        open={booking !== null}
        initialFrom={booking?.initialFrom}
        bookings={bookings ?? []}
        locked={locked ?? []}
        today={today}
        onClose={() => {
          setBooking(null);
        }}
      />
    </div>
  );
};

const prevOf = (year: number, month: number): [number, number] =>
  month === 1 ? [year - 1, 12] : [year, month - 1];
const nextOf = (year: number, month: number): [number, number] =>
  month === 12 ? [year + 1, 1] : [year, month + 1];
